import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAgents } from "@/components/Agents/AgentsContext";
import {
  agentMailboxAddress,
  mailboxFromLocalPart,
  orgHasMailConnection,
  type OrgAgent,
} from "@/lib/agentsDemoData";
import { agentsHref } from "@/lib/atwAgentsHost";

type EmailPanel = "existing" | "create";

function splitAddress(address: string): { local: string; domain: string } {
  const at = address.indexOf("@");
  if (at <= 0) return { local: "", domain: "" };
  return { local: address.slice(0, at), domain: address.slice(at + 1) };
}

export function AgentEmailSection({ agent }: { agent: OrgAgent }) {
  const { org, may, setAgentMailbox } = useAgents();
  const canEdit = may("manage_agent_configuration");
  const hasMail = orgHasMailConnection(org.connections);
  const mailbox = agentMailboxAddress(agent);
  const [editing, setEditing] = useState(false);
  const [panel, setPanel] = useState<EmailPanel | null>(null);
  const [address, setAddress] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [domain, setDomain] = useState("");

  const showChooser = hasMail && (!mailbox || editing);

  const openExisting = () => {
    setPanel("existing");
    if (!address && mailbox && agent.mailboxSource !== "created") setAddress(mailbox);
  };

  const openCreate = () => {
    setPanel("create");
    if (!localPart && !domain && mailbox && agent.mailboxSource === "created") {
      const parts = splitAddress(mailbox);
      setLocalPart(parts.local);
      setDomain(parts.domain);
    }
  };

  const saveExisting = (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    const saved = setAgentMailbox(agent.id, { mailboxAddress: address, mailboxSource: "existing" });
    if (!saved) {
      toast.error("Enter a valid email address.");
      return;
    }
    toast.success("Mailbox saved on this agent.");
    setEditing(false);
    setPanel(null);
  };

  const saveCreated = (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    const next = mailboxFromLocalPart(localPart, domain);
    if (!next) {
      toast.error("Enter a local part and a domain, such as agent and example.com.");
      return;
    }
    const saved = setAgentMailbox(agent.id, { mailboxAddress: next, mailboxSource: "created" });
    if (!saved) {
      toast.error("Enter a local part and a domain, such as agent and example.com.");
      return;
    }
    toast.success("Mailbox saved on this agent.");
    setEditing(false);
    setPanel(null);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Email</CardTitle>
        <CardDescription>The address this agent sends as.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">
        {!hasMail ? (
          <div className="space-y-3">
            <p>
              A mail connection is the pipe this agent sends through. Add ATW Mail, Google, or
              Microsoft on Connections first. You can choose an address after that connection
              exists.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link
                to={agentsHref("/connections")}
                className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
              >
                Connections
              </Link>
              <Link
                to={agentsHref("/connections/new")}
                className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
              >
                Add a connection
              </Link>
            </div>
          </div>
        ) : null}

        {hasMail && mailbox && !editing ? (
          <div className="space-y-3">
            <p className="text-gray-900 dark:text-white">
              Sends as <span className="font-medium">{mailbox}</span>.
            </p>
            <p>
              {agent.mailboxSource === "created"
                ? "This address was entered for the agent."
                : "This is an existing email address."}{" "}
              It is saved on this agent. It was not created at Google, Microsoft, or ATW Mail.
            </p>
            {canEdit ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditing(true);
                  if (agent.mailboxSource === "created") {
                    const parts = splitAddress(mailbox);
                    setLocalPart(parts.local);
                    setDomain(parts.domain);
                    setPanel("create");
                  } else {
                    setAddress(mailbox);
                    setPanel("existing");
                  }
                }}
              >
                Change
              </Button>
            ) : (
              <p>A Super Admin can change this address.</p>
            )}
          </div>
        ) : null}

        {showChooser ? (
          <div className="space-y-3">
            {mailbox && editing ? (
              <p>
                Current address: <span className="text-gray-900 dark:text-white">{mailbox}</span>
              </p>
            ) : (
              <p>
                Choose how this agent gets its address. Saving stores the address on this agent.
                It does not create a mailbox at Google, Microsoft, or ATW Mail.
              </p>
            )}
            {canEdit ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={panel === "existing" ? "default" : "outline"}
                    onClick={openExisting}
                  >
                    Use an existing email
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={panel === "create" ? "default" : "outline"}
                    onClick={openCreate}
                  >
                    Create an email
                  </Button>
                  {editing ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(false);
                        setPanel(null);
                      }}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </div>
                {panel === "existing" ? (
                  <form className="space-y-3" onSubmit={saveExisting}>
                    <label className="block">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Email address
                      </span>
                      <Input
                        className="mt-1 w-full max-w-md"
                        type="email"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="name@example.com"
                        autoComplete="off"
                      />
                    </label>
                    <Button type="submit" size="sm">
                      Save
                    </Button>
                  </form>
                ) : null}
                {panel === "create" ? (
                  <form className="space-y-3" onSubmit={saveCreated}>
                    <p>Enter the local part and domain. This only saves the address here.</p>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="block">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Local part
                        </span>
                        <Input
                          className="mt-1 w-40"
                          value={localPart}
                          onChange={(e) => setLocalPart(e.target.value)}
                          placeholder="agent"
                          autoComplete="off"
                        />
                      </label>
                      <span className="pb-2 text-gray-500" aria-hidden>
                        @
                      </span>
                      <label className="block">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Domain</span>
                        <Input
                          className="mt-1 w-48"
                          value={domain}
                          onChange={(e) => setDomain(e.target.value)}
                          placeholder="example.com"
                          autoComplete="off"
                        />
                      </label>
                    </div>
                    <Button type="submit" size="sm">
                      Save
                    </Button>
                  </form>
                ) : null}
              </>
            ) : (
              <p>A Super Admin can choose the address.</p>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
